import MultiStrikeChart from '../components/multiStikeChart/chart'
import { createSearchParams, SetURLSearchParams } from 'react-router-dom'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import {
    CallPutType,
    MultiStrikeChartTypes,
    MultiStrikeSecuritiesType,
    setMultiStrikeChartTimeBucket,
    setMultiStrikeChartType,
    setMultiStrikeCumOi,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities,
    setMultiStrikeShowChange
} from '../reducer/multiStrikeChart.reducer'
import { selectMultiStrikeAtm, selectMultiStrikeChartSecurities, selectMultiStrikeChartTimebucket, selectMultiStrikeCumOi, selectMultiStrikeNearAtm } from '../selectors/multiStrikeChart'
import store from '../store'
import { ChartViewKey } from '../assets/constants'
import { selectUnderlyingOptionsExpiriesV2 } from '../selectors/options'
import { selectPrimarySecurity } from '../selectors/home'
import { getSecurityKey, isFnOSecurity } from './security'
import { findNearestNumber } from './tradepanel'
import { getStrikeAndStrikeData } from '../pages/home/openInterestHeader/MultiStrikesMenu/MultiStrikesMenu'

class MultistrikeChartManager {
    charts: Record<string, MultiStrikeChart> = {}
    setSearchParams?: SetURLSearchParams

    setChart(chartId: string, chart: MultiStrikeChart) {
        this.charts[chartId] = chart
    }

    getChart(chartId: string) {
        return this.charts[chartId]
    }

    setSearchParamsMethod(method: SetURLSearchParams) {
        this.setSearchParams = method
    }

    unsetSearchParamsMethod(method: SetURLSearchParams) {
        this.setSearchParams = undefined
    }

    updateTimeBucket(bucket: SuperChartConfiguration_TimeBucket) {
        const state = store.getState()
        const chartTimeBucket = selectMultiStrikeChartTimebucket(state)

        if (chartTimeBucket.timeSeries !== bucket.timeSeries || chartTimeBucket.timeValue !== bucket.timeValue) {
            store.dispatch(setMultiStrikeChartTimeBucket(bucket))
        }
    }

    updateChartType(chartType: MultiStrikeChartTypes) {
        const cumOi = selectMultiStrikeCumOi(store.getState())

        store.dispatch(setMultiStrikeChartType(cumOi && chartType === 'oi' ? 'cumOi' : chartType))
    }

    updateCumOi(cumOi: boolean) {
        store.dispatch(setMultiStrikeCumOi(cumOi))
    }

    updateShowChange(showChange: boolean) {
        store.dispatch(setMultiStrikeShowChange(showChange))
    }

    setAtmUndefined() {
        store.dispatch(
            setMultiStrikeNearAtm({
                nearAtm: undefined,
                atm: undefined
            })
        )
    }

    updateExpiry(exp: string) {
        const location = window.location
        const searchParams = createSearchParams(location.search)
        searchParams.set('expiry', exp)
        this.setSearchParams && this.setSearchParams(searchParams)
    }

    async updateNumOfStrikes(num: number) {
        if (this.charts[ChartViewKey.MULTI_STRIKES_CHART]) {
            const chart = this.charts[ChartViewKey.MULTI_STRIKES_CHART]
            const searchParams = createSearchParams(location.search)
            const expiry = searchParams.get('expiry') || ''
            const state = store.getState()
            const { primary, underlying } = selectPrimarySecurity(state)
            const mainSecurity = primary && isFnOSecurity(primary) ? underlying : primary
            const underlyingToken = getSecurityKey(mainSecurity?.exchange ?? 0, mainSecurity?.code ?? '')
            const underlyingSnaphot = state.securityStore.snapshots[underlyingToken || '']
            const optionsExpiries = selectUnderlyingOptionsExpiriesV2(underlyingToken)(state)
            const expiryIndex = optionsExpiries?.findIndex((o) => o.expiry.name === expiry)
            const nearAtm = selectMultiStrikeNearAtm(state)
            const atmSelected = selectMultiStrikeAtm(state)

            if (expiryIndex !== undefined && expiryIndex >= 0) {
                const expiryData = optionsExpiries?.[expiryIndex]
                if (expiryData) {
                    const atmStrike = findNearestNumber(expiryData.strikesArray, underlyingSnaphot?.lastTradedPrice)
                    const atm = expiryData.strikes.find((s) => s.strike === atmStrike)
                    const atmIndex = expiryData.strikes.findIndex((s) => s.strike === atmStrike)
                    if (atm) {
                        if (atmSelected === atm.strike && num === nearAtm) return
                        store.dispatch(
                            setMultiStrikeNearAtm({
                                nearAtm: num,
                                atm: atm.strike
                            })
                        )
                        const { strike, strikeData } = getStrikeAndStrikeData(atm, mainSecurity?.priceMultiplier ?? 100, false)
                        let data = {
                            [strike]: strikeData
                        }
                        const a = [3, 5, 7, 9]
                        a.forEach((x) => {
                            if (num >= x) {
                                const index = x === 3 ? 1 : x === 5 ? 2 : x === 7 ? 3 : 4
                                const atm1 = expiryData.strikes[atmIndex - index]
                                const atm2 = expiryData.strikes[atmIndex + index]
                                const { strike: strike1, strikeData: strikeData1 } = getStrikeAndStrikeData(atm1, mainSecurity?.priceMultiplier ?? 100, false)
                                const { strike: strike2, strikeData: strikeData2 } = getStrikeAndStrikeData(atm2, mainSecurity?.priceMultiplier ?? 100, false)
                                data = {
                                    [strike1]: strikeData1,
                                    ...data,
                                    [strike2]: strikeData2
                                }
                            }
                        })
                        chart.dataManager.removeAllSecondarySecurities()
                        const formattedData: MultiStrikeSecuritiesType = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, value.callPut]))
                        store.dispatch(setMultiStrikeSecurities(formattedData))
                        await chart.dataManager.addNewStrikes(data)
                        chart.onCrosshairMove({} as any)
                        chart.resetTimeScale()
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => chart.restoreAutoscroll())
                        })
                    }
                }
            }
        }
    }
    addOrRemoveSecurity = (
        strike: string,
        strikeData: {
            callSecKey: string
            putSecKey: string
            callPut: CallPutType[]
        }
    ) => {
        if (this.charts[ChartViewKey.MULTI_STRIKES_CHART]) {
            const chart = this.charts[ChartViewKey.MULTI_STRIKES_CHART]
            const state = store.getState()
            const multiStrikeChartSecurities = selectMultiStrikeChartSecurities(state)
            const multiStrikeChartSecuritiesKeys = Object.keys(multiStrikeChartSecurities ?? {})
            const data = { ...multiStrikeChartSecurities }

            if (data[strike]) {
                delete data[strike]
                if (chart) chart.dataManager.removeStrike(strike)
            } else {
                if (multiStrikeChartSecuritiesKeys.length < 10) {
                    data[strike] = strikeData.callPut
                    if (chart) chart.dataManager.addNewSecurities(strike, strikeData)
                }
            }
            store.dispatch(setMultiStrikeSecurities(data))
            setTimeout(() => {
                chart?.onCrosshairMove({} as any)
            }, 500)
        }
    }
}

export default MultistrikeChartManager
